import { useCallback, useEffect, useRef, useState } from 'react';

import type { BloomRuntimeConfig, Tv2mLinkContext } from '@apm/contracts';

import type { AuthApi } from '../auth/use-auth.js';
import type { ChannelChoice } from '../channel-selection.js';
import { channelForNumber } from '../channel-keys.js';
import { readAppDisplay, resolveClientType } from '../client-type.js';
import { ChannelSheet } from '../components/ChannelSheet.js';
import { ChannelCard } from '../components/home/ChannelCard.js';
import { ItemCard } from '../components/home/ItemCard.js';
import { Masthead } from '../components/home/Masthead.js';
import { Rail } from '../components/home/Rail.js';
import { SideNav } from '../components/home/SideNav.js';
import { SettingsSheet } from '../components/SettingsSheet.js';
import type { EpgRow } from '../epg.js';
import { config } from '../config.js';
import { captureFreezeFrame } from '../freeze-frame.js';
import { composeHome } from '../home/compose-home.js';
import { useHomePreviewEnabled } from '../home/home-preview-enabled.js';
import { previewAllowed } from '../home/preview-policy.js';
import { fetchPreviewSource } from '../home/preview-source.js';
import type { PreviewSource } from '../home/preview-source.js';
import { readSaveData, useHomePreview } from '../home/use-home-preview.js';
import { lastWatchedChannelId, resolveWatchTarget } from '../home/watch-target.js';
import { useT } from '../i18n/LocaleProvider.js';
import { playbackBuffers, unlockBuffers } from '../playback-buffers.js';
import { healAfterClose } from '../tv-focus/use-tv-focus.js';
import { useChannelNumberKeys } from '../use-channel-keys.js';
import type { Tv2mApi } from '../useTv2m.js';

/**
 * 홈 (v32 · `docs/63` · 절대 규칙 81·82). **시작 게이트의 자리에 선다** — `ChannelGate` 가
 * `entrySurface` 로 이 화면과 시청 화면을 가른다. 여기 있는 동안 `WatchScreen` 은 없고
 * 리시버도 없다 — 그래서 `<video>` 한 쌍이 비어 있고 프리뷰가 그중 하나를 쓸 수 있다.
 *
 * ── 재료는 편성표와 같다 ────────────────────────────────────
 *
 * `rows` 는 `toEpgRows` 의 것이고 `ChannelGate` 가 이미 받은 세 응답에서 나온다. 홈이
 * 서버에 새로 묻는 것은 프리뷰의 주소 하나뿐이다(HM7 · `use-home-preview.ts`).
 *
 * ── 홈은 지점을 넘기지 않는다 ───────────────────────────────
 *
 * OK 가 부르는 것은 `ChannelGate` 의 문 둘 — 대상 채널이면 `onWatchTarget(channelId, at)`
 * (저장하지 않는다 · `?channel=` 은 저장하지 않는다는 `docs/23` §7.3), 다른 채널이면
 * `onPickChannel(channelId)`(저장한다). 재생의 시작점은 행이 정한다(규칙 68·70).
 *
 * ── 이 화면은 층이 아니다 ───────────────────────────────────
 *
 * `data-focus-scope` 를 만들지 않는다. 설정 시트와 편성표는 자기 층·자기 화면을 갖는다.
 * 뒤로 키는 엔진 그대로 넘긴다(`history.back()` 을 부르지도 막지도 않는다 · `docs/63` §6.4).
 */
/**
 * 시청에서 돌아온 홈이 편성표를 다시 받기까지의 지연 (`docs/63` §3.3 ⑦). 떠난 채널의 `teardown`
 * 보고와 `GET /v1/channels/lineup` 이 같은 순간에 나가면 어느 쪽이 먼저 서버에 닿을지 정해져
 * 있지 않다(규칙 73 ② 의 그 경합) — 그때 이어보기 문구가 낡는다. **첫 안 1000** 이고 값은
 * HM0 실기 ②가 정한다(`docs/64` §0.2).
 */
export const HOME_REFRESH_DELAY_MS = 1000;

export function HomeScreen({
  rows,
  choice,
  favorites,
  runtime,
  tv2m,
  auth,
  lastContext,
  onOpenLast,
  onWatchTarget,
  onPickChannel,
  onToggleFavorite,
  onEpgOpen,
  onPickFromGuide,
  onRefresh,
}: {
  rows: readonly EpgRow[];
  choice: ChannelChoice;
  favorites: readonly string[];
  runtime: BloomRuntimeConfig;
  tv2m: Tv2mApi;
  auth: AuthApi;
  lastContext: Tv2mLinkContext | null;
  onOpenLast: () => void;
  /** 대상 채널로 들어간다. `at` 은 「지금 판매 중」 카드의 상품이고 그 밖은 `null` */
  onWatchTarget: (channelId: string, at: string | null) => void;
  /** 다른 채널로 들어간다 — 사람이 골랐으므로 저장한다 */
  onPickChannel: (channelId: string) => void;
  onToggleFavorite: (channelId: string) => void;
  /** 편성표 시트를 여는 순간 편성을 다시 받는다 (절대 규칙 73 ②) */
  onEpgOpen: () => void;
  /** 편성표 시트에서 고른 것 — 사람이 골랐으므로 저장한다. `at` 은 첫 장이 아닐 때의 회차 */
  onPickFromGuide: (channelId: string, at: string | null) => void;
  /** 시청에서 돌아왔다 — 지연 뒤 편성표를 다시 받는다. 첫 진입은 `null` */
  onRefresh: (() => void) | null;
}) {
  const t = useT();
  const [clientType] = useState(() => resolveClientType());
  const [display] = useState(() => readAppDisplay());
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** 편성표 — 재생 중 쓰는 **그 시트**(`ChannelSheet` · 닫기 = `data-focus-back`)를 홈 위에 연다 */
  const [guideOpen, setGuideOpen] = useState(false);
  /** 시트를 연 버튼. 닫히면 거기로 돌아간다 — 안 하면 `<body>` 로 떨어져 「리모컨이 죽었다」와 구분되지 않는다 */
  const opener = useRef<HTMLElement | null>(null);
  const [nowMs] = useState(() => Date.now());
  const handheld = display.handheld || clientType === 'mobile_web';

  /**
   * ★ 첫 포커스는 CTA 다 — **OK 한 번으로 이어 재생**이어야 한다(2026-09-10 실기). `autoFocus`
   * 가 이미 그 일을 하지만 마운트 뒤 한 프레임 더 못박는다 — 포커스가 `<body>` 에 있으면 엔진이
   * 첫 OK 를 **삼키고**(층 밖 포커스 · 절대 규칙 32) CTA 로 옮기기만 해 OK 가 두 번 든다.
   */
  useEffect(() => {
    if (handheld) return;
    const raf = requestAnimationFrame(() => {
      const cta = document.querySelector<HTMLElement>('[data-testid=start-watching]');
      if (cta !== null && document.activeElement !== cta) cta.focus();
    });
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [handheld]);

  const openGuide = useCallback(() => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    onEpgOpen();
    setGuideOpen(true);
  }, [onEpgOpen]);
  const closeSheet = useCallback((setOpen: (open: boolean) => void) => {
    setOpen(false);
    const back = opener.current;
    opener.current = null;
    requestAnimationFrame(() => {
      if (back !== null && back.isConnected) back.focus();
      else healAfterClose();
    });
  }, []);

  useEffect(() => {
    if (onRefresh === null) return;
    const timer = window.setTimeout(onRefresh, HOME_REFRESH_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [onRefresh]);

  /*
   * 대상 채널 — `?channel=` > **계정의 최근 시청**(로그인했을 때 · `resumeUpdatedAt`) > 기기 저장값 >
   * 첫 행. 비로그인이면 계정 축이 없다(규칙 46) — 기기의 저장값 그대로다.
   */
  const target = resolveWatchTarget(choice, rows, {
    preferred: config.channelId,
    lastWatched: auth.token === null ? null : lastWatchedChannelId(rows),
  });

  /*
   * 프리뷰 (`docs/63` §5). 켤지는 순수 함수가 정하고(`previewAllowed`) 사실은 여기서 읽는다.
   * 주소는 대상 채널마다 한 번 받는다 — `target` 은 렌더마다 새 객체라(`toEpgRows`) 의존성은
   * 채널 id 다. 대상이 바뀌지 않으면 다시 받지 않는다.
   */
  const [previewEnabled] = useHomePreviewEnabled();
  const allowed = previewAllowed({
    handheld,
    saveData: readSaveData(),
    userEnabled: previewEnabled,
  });
  const targetId = target?.channel.channelId ?? null;
  const targetRef = useRef(target);
  targetRef.current = target;
  const [source, setSource] = useState<PreviewSource | null>(null);
  useEffect(() => {
    const row = targetRef.current;
    if (!allowed || row === null) {
      setSource(null);
      return;
    }
    const controller = new AbortController();
    void fetchPreviewSource(row, {
      serverUrl: config.serverUrl,
      deviceId: tv2m.init?.deviceId,
      token: auth.token,
      signal: controller.signal,
      // 홈쇼핑은 「지금」 카드의 상품부터 — 계정 행(서버가 돌린 첫 장) 또는 비로그인의 기기 기억
      startFromProductId:
        row.channel.kind === 'homeshopping' ? (row.lineup?.items[0]?.productId ?? null) : null,
    }).then((found) => {
      if (!controller.signal.aborted) setSource(found);
    });
    return () => {
      controller.abort();
    };
  }, [allowed, targetId, tv2m.init, auth.token]);

  const model = composeHome({
    rows,
    target,
    favorites,
    previewUrl: source?.url ?? null,
    previewStartSec: source?.startSec ?? 0,
    previewAllowed: allowed,
    nowMs,
  });

  const [previewHost, setPreviewHost] = useState<HTMLDivElement | null>(null);
  const preview = useHomePreview({
    container: previewHost,
    url: model.masthead.kind === 'preview' ? model.masthead.url : null,
    startSec: model.masthead.kind === 'preview' ? model.masthead.startSec : 0,
  });

  /**
   * OK — 전환 절차 (`docs/63` §3.3). **`unlockBuffers()` 가 제스처 안이다** — 실제 `start()` 는
   * 새 화면의 `useEffect` 라 제스처 밖이고, 그때 아이폰의 증상은 `PLAYBACK_FAILED` 다(v12 가
   * 채널 전환에서 만난 그 모양 · §11-4).
   */
  const watchTarget = useCallback(
    (at: string | null) => {
      if (target === null) return;
      // ① 프리뷰가 **그 채널의 그 항목**을 틀고 있을 때만 마지막 프레임을 잡는다(§5.6) — 상품
      //    카드는 다른 항목이라 잡지 않는다(다른 그림이 한 프레임 보인다 · §11-13).
      if (at === null && preview.state === 'playing') captureFreezeFrame(playbackBuffers());
      // ② 페이지 전환 전에 프리뷰를 끝낸다 — 어댑터가 src 가 남은 엘리먼트를 입양하지 않게(§11-3)
      preview.halt();
      // ③ 제스처 안의 허가
      unlockBuffers();
      onWatchTarget(target.channel.channelId, at);
    },
    [target, preview, onWatchTarget],
  );
  const pickChannel = useCallback(
    (channelId: string) => {
      if (target !== null && channelId === target.channel.channelId) {
        watchTarget(null);
        return;
      }
      preview.halt();
      unlockBuffers();
      onPickChannel(channelId);
    },
    [target, preview, watchTarget, onPickChannel],
  );

  // 숫자 키 — 옛 게이트가 듣던 것과 같은 훅 (절대 규칙 71 · `docs/63` §6.5). 기본 층에서만.
  useChannelNumberKeys(
    useCallback(
      (number: number) => {
        const next = channelForNumber(rows, number);
        if (next === null) return;
        pickChannel(next);
      },
      [rows, pickChannel],
    ),
  );

  return (
    <div className="home" data-testid="home-root" data-client-type={clientType}>
      <div className="home__body">
        <Masthead
          model={model.masthead}
          previewHost={setPreviewHost}
          previewShowing={preview.state === 'playing'}
          autoFocus={!handheld}
          onWatch={() => {
            watchTarget(null);
          }}
        />
        {model.rails.map((rail) => {
          switch (rail.id) {
            case 'favorites':
            case 'channels':
              return (
                <Rail
                  key={rail.id}
                  id={rail.id}
                  title={
                    rail.id === 'favorites' ? t('home.rail.favorites') : t('home.rail.channels')
                  }
                  count={rail.cards.length}
                >
                  {rail.cards.map((card) => (
                    <ChannelCard
                      key={card.row.channel.channelId}
                      row={card.row}
                      now={card.now}
                      onPick={pickChannel}
                    />
                  ))}
                </Rail>
              );
            case 'selling':
              return (
                <Rail
                  key={rail.id}
                  id={rail.id}
                  title={t('home.rail.selling')}
                  count={rail.cards.length}
                >
                  {rail.cards.map((card) => (
                    <ItemCard
                      key={card.item.productId}
                      item={card.item}
                      onPick={(productId) => {
                        watchTarget(productId);
                      }}
                    />
                  ))}
                </Rail>
              );
          }
        })}
      </div>

      <SideNav
        onOpenGuide={rows.length > 0 ? openGuide : null}
        onOpenSettings={() => {
          opener.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
          setSettingsOpen(true);
        }}
      />

      {guideOpen && (
        <ChannelSheet
          rows={rows}
          /* 홈에서는 아무것도 재생되고 있지 않다 — 「보는 중」도 앵커도 없다(규칙 46·73) */
          currentChannelId={null}
          currentProductId={null}
          favorites={favorites}
          onToggleFavorite={onToggleFavorite}
          signedIn={auth.token !== null}
          now={null}
          onPick={(channelId, productId, isEntryCard) => {
            // 카드 클릭이 제스처다 — 옛 게이트의 「방송편성 보기」와 같은 줄(v25).
            preview.halt();
            unlockBuffers();
            onPickFromGuide(channelId, isEntryCard ? null : productId);
          }}
          onClose={() => {
            closeSheet(setGuideOpen);
          }}
        />
      )}

      {settingsOpen && (
        <SettingsSheet
          init={tv2m.init}
          subscribed={tv2m.pushSubscribed}
          linkedToAccount={tv2m.linkedToAccount}
          runtime={runtime}
          auth={auth}
          ready={tv2m.ready}
          needsIosInstall={tv2m.needsIosInstall}
          lastContext={lastContext}
          /*
           * 둘 다 `null` — 홈에는 재생 중인 채널이 없어 광고·상품 줄이 뜻이 없다(그 둘은 채널의
           * 정책이다). 없는 것을 끌 수 있게 보이지 않는다(규칙 56·66 의 모양).
           */
          adPolicy={null}
          offerPolicy={null}
          onOpenLast={onOpenLast}
          onOpenChannels={
            rows.length > 0
              ? () => {
                  // 설정 시트를 닫고 편성표 시트를 연다. 둘을 겹쳐 띄우지 않는다.
                  setSettingsOpen(false);
                  onEpgOpen();
                  setGuideOpen(true);
                }
              : null
          }
          onEnablePush={tv2m.enablePush}
          onClose={() => {
            closeSheet(setSettingsOpen);
          }}
        />
      )}
    </div>
  );
}
